import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import Switch from "@mui/material/Switch";
import Request from "common/Request";
import ActionsContext from "common/ActionsContext";
import {
  getApplicationMetricsMock,
  parseApplicationMetrics,
} from "views/Statistics/DPI/ApplicationMetrics/api";
import BoxplotMultiple from "common/BoxplotMultiple";
import LineChartOrdinal from "common/graphs/LineChartOrdinal";
import { safeStr } from "common/api";
import SelectMultiAutocomplete from "common/SelectMultiAutocomplete";
import { parsePolicyNames } from "views/Statistics/DPI/ApplicationMetrics/api";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import { AppCharts } from "views/Statistics/DPI/ApplicationMetrics/ApplicationMetrics.jsx";
import TimeCharts from "views/Statistics/DPI/ApplicationMetrics/TimeCharts";

const ChartWrapper = styled.div`
  margin: 0 20px;
`;

const WrapperCharts = styled.div`
  margin-top: -20px;

  h4 {
    font-size: 14px !important;
    padding: 0;
  }

  .subscribergroup-dashboard {
    padding: 0px;
  }
`;

const LayoutSelectors = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: 800px) {
    flex-direction: column;
    width: 100%;
    > div {
      width: 100%;
    }
  }
`;

const LayoutRight = styled.div`
  display: flex;
  align-items: center;
  margin-right: -20px;
`;

const ChartTitle = styled.h4`
  margin-top: 0;
  margin-bottom: 0;
  font-weight: 100;
  font-family: "AllianceNo2-SemiBold";
  font-size: 14px !important;
  color: var(--Text-Primary);
`;

const Title = styled.div`
  width: 350px;

  @media (max-width: 880px) {
    width: 100%;
  }
`;

const SelectorsWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  @media (max-width: 880px) {
    margin-top: -10px;
  }
`;

const HeaderWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media (max-width: 880px) {
    flex-direction: column;
    width: 100%;
  }

  h4 {
    white-space: nowrap;
  }

  .sw-input.MuiSwitch-root {
    & .Mui-checked {
      color: var(--switch-checked-green);
    }

    & .Mui-checked + .MuiSwitch-track {
      background-color: var(--switch-checked-light-green);
    }
  }
`;

const MultiSelectorsWrapper = styled.div`
  width: 100%;
  display: flex;
  margin-right: 40px;
  align-items: center;
  & > div {
    width: 100%;
    margin-right: 10px;
  }
  @media (max-width: 800px) {
    flex-direction: column;
    & > div {
      width: 100%;
      margin-right: 0px;
    }
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  margin-right: 5px;
  border-bottom: none !important;
  padding: 20px 0 !important;
  position: relative;

  &.dpispeed {
    .header-dropdown {
      right: 10px !important;
      top: 10px !important;
    }
  }
`;

const SwitchWithLabel = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin: 0 20px 0 5px;
`;

const Label = styled.div`
  margin-right: 10px;
  font-family: AllianceNo2-SemiBold !important;
  font-weight: 100;
  color: var(--Text-Primary);
  width: 100px;
  text-align: right;
`;

const RefreshButton = styled.div`
  cursor: pointer;
  height: 20px;
  width: 0px;
`;

const SelectWrapper = styled.div`
  width: 250px;
  margin-right: 20px;
`;

const _displayChoices = [
  { value: "updown", label: "Downlink & Uplink Speed per Flow" },
  { value: "downall", label: "Downlink Speed & Time Evolution" },
  { value: "upall", label: "Uplink Speed & Time Evolution" },
];

function getCmdSubscriber(target) {
  if (!target) return "all";
  const { addr, subsId } = target;
  return addr ? addr : `id ${subsId}`;
}

function getCmdSubscribergroup(group) {
  if (!group) return "all";
  return `name '"${safeStr(group).replaceAll("'", "")}"'`;
}

function getPolicyFilterCmd(policiesRateSelected) {
  if (policiesRateSelected.length === 0) {
    return "all";
  } else {
    return `name '${policiesRateSelected
      .map((policy) => `"${policy.replaceAll('"', '""')}"`)
      .join(",")}'`;
  }
}

function DPISpeed({ hours, test = false, target, subscriberGroup }) {
  const actions = useContext(ActionsContext);
  const [plotMode, setPlotMode] = useState("boxplot");
  const [request, setRequest] = useState(null);
  const cmdSubscriber = getCmdSubscriber(target);
  const [policiesRate, setPoliciesRate] = useState([]);
  const [policiesRateSelected, setPoliciesRateSelected] = useState([]);
  const [display, setDisplay] = useState("downall");
  const [showMinMax, setShowMinMax] = useState(false);

  useEffect(() => {
    doLoad();

    return actions.recv("do-load", doLoad);
  }, [hours, JSON.stringify(policiesRateSelected)]);

  useEffect(() => {
    doLoadFilters();
  }, []);

  function doLoad() {
    const promise = (
      test
        ? Promise.resolve(getApplicationMetricsMock())
        : ifCl.run(
            `show pkteng app-metrics hours ${hours} categories 19 subscriber ${getCmdSubscriber(
              target
            )} policy ${getPolicyFilterCmd(
              policiesRateSelected
            )} subscriber-group ${getCmdSubscribergroup(
              subscriberGroup
            )} percentile`,{ timeout: DPI_STATS_TMOUT_MS }
          )
    )
      .then(parseApplicationMetrics)
      .catch((error) => {
        showModalError("Error", error);
      });

    setRequest(promise);
  }

  function doLoadFilters() {
    ifCl.run("show policy rate").then((policiesResp) => {
      setPoliciesRate(parsePolicyNames(policiesResp));
    });
  }

  return (
    <>
      <HeaderWrapper>
        <Title>
          <h4>Application Speed Metrics</h4>
        </Title>
        <SelectorsWrapper>
          <MultiSelectorsWrapper>
            <SelectMultiAutocomplete
              icon="folder_open"
              title="Rate policy"
              name="policy-rate-selector"
              placeholder="Rate policy"
              options={policiesRate.map((name) => {
                return { label: name, value: name };
              })}
              key="policyRateSelector"
              onChange={(value) =>
                setPoliciesRateSelected(
                  value.map((optionType) => optionType.value)
                )
              }
            />
          </MultiSelectorsWrapper>
          <HeaderRight className="header dpispeed">
            <RefreshButton
              className="header-dropdown"
              onClick={() => actions.send("do-load")}
            >
              <a
                data-toggle="cardloading"
                data-loading-effect="pulse"
                title="Refresh"
              >
                <i class="material-icons">refresh</i>
              </a>
            </RefreshButton>
          </HeaderRight>
        </SelectorsWrapper>
      </HeaderWrapper>
      <LayoutSelectors>
        <SelectWrapper className="displaySelect">
          <SelectInputWithIcon
            title="Display"
            name="Display"
            icon="apps"
            selected={display}
            onChange={({ target }) => setDisplay(target.value)}
            options={_displayChoices}
          />
        </SelectWrapper>
        <LayoutRight>
          <SwitchWithLabel>
            <Label>
              {plotMode === "boxplot" ? "Line chart view" : "Boxplot view"}
            </Label>
            <Switch
              className="sw-input"
              checked={plotMode !== "boxplot"}
              onChange={() =>
                setPlotMode(plotMode === "boxplot" ? "line" : "boxplot")
              }
            />
          </SwitchWithLabel>
          <SwitchWithLabel>
            <Label>View max+min</Label>{" "}
            <Switch
              className="sw-input"
              checked={showMinMax}
              onChange={() => setShowMinMax(!showMinMax)}
            />
          </SwitchWithLabel>
        </LayoutRight>
      </LayoutSelectors>
      <Request during={request}>
        {(result) => {
          return (
            <WrapperCharts>
              <AppCharts
                data={result}
                plotMode={plotMode}
                info={{
                  titleDown: "Downlink speed per flow",
                  titleUp: "Uplink speed per flow",
                }}
                display={display}
                showMinMax={showMinMax}
                className="subscribergroup-dashboard"
              />
              {display !== "updown" && (
                <TimeCharts
                  data={result}
                  plotMode={plotMode}
                  hours={hours}
                  display={display}
                  showMinMax={showMinMax}
                  target={target}
                  subscriberGroups={subscriberGroup ? [subscriberGroup] : []}
                  className="subscriber-dashboard"
                />
              )}
            </WrapperCharts>
          );
        }}
      </Request>
    </>
  );
}

export default DPISpeed;
